// API Configuration
import { getAuthHeader } from './auth';

export const resolveBrowserApiBaseUrl = (configuredBaseUrl, hostname) => {
  if (!hostname) return configuredBaseUrl;
  // Browsers always use same-origin /api. Local Vite forwards it to this
  // worktree's derived API port, avoiding fragile cross-port browser traffic.
  return '';
};

const browserHostname = typeof window === 'undefined' ? '' : window.location.hostname;
const configuredApiBaseUrl = import.meta.env.VITE_API_URL ?? 'http://localhost:8000';
const API_BASE_URL = resolveBrowserApiBaseUrl(configuredApiBaseUrl, browserHostname);
const TEMPLATE_PUBLISH_API_BASE_URL = import.meta.env.VITE_TEMPLATE_PUBLISH_API_URL || API_BASE_URL;

export const API_URL = API_BASE_URL;

// The production site forwards /api through a Vercel rewrite, which ends a
// proxied request after about two minutes. A Slides turn often streams for
// five or more, so the live stream (and the post-apply review that depends on
// it) was cut off (production a41e4bad, 20f84926). The long-lived chat stream
// therefore connects to the API origin directly; every other call keeps the
// same-origin path. The API's CORS policy already allows these origins and
// the stream authenticates with a bearer header, not cookies.
const PRODUCTION_STREAM_ORIGIN = 'https://niki-agent-production.up.railway.app';
export const resolveStreamApiBaseUrl = (configuredStreamUrl, hostname, sameOriginBase) => {
  if (configuredStreamUrl) return configuredStreamUrl;
  if (hostname === 'nikistudio.cloud' || hostname === 'www.nikistudio.cloud') {
    return PRODUCTION_STREAM_ORIGIN;
  }
  return sameOriginBase;
};
export const STREAM_API_URL = resolveStreamApiBaseUrl(
  import.meta.env.VITE_STREAM_API_URL, browserHostname, API_BASE_URL,
);
export const TEMPLATE_PUBLISH_API_URL = TEMPLATE_PUBLISH_API_BASE_URL;

export const apiCall = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  return authenticatedApiCall(url, options);
};

export const templatePublishApiCall = async (endpoint, options = {}) => {
  const url = `${TEMPLATE_PUBLISH_API_BASE_URL}${endpoint}`;
  return authenticatedApiCall(url, options);
};

const authenticatedApiCall = async (url, options = {}) => {

  const { suppressErrorStatuses = [], ...fetchOptions } = options;

  if (import.meta.env.DEV) {
    console.debug(`[api] ${fetchOptions.method || 'GET'} ${url}`);
  }

  const isFormData = typeof FormData !== 'undefined' && fetchOptions.body instanceof FormData;
  const headers = {
    ...fetchOptions.headers,
  };

  // Let the browser set the multipart boundary for FormData uploads.
  // For JSON requests we still default to application/json.
  if (!isFormData && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  headers['Authorization'] = getAuthHeader();

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      headers,
    });

    const contentType = response.headers.get('content-type') || '';
    const rawBody = await response.text();
    const preview = rawBody?.slice(0, 200);

    if (!response.ok) {
      if (!suppressErrorStatuses.includes(response.status)) {
        console.error(`API Error: ${url} - Status: ${response.status}`, preview);
      }
      return null;
    }

    if (!rawBody) {
      return null;
    }

    if (contentType.includes('application/json') || rawBody.trim().startsWith('{') || rawBody.trim().startsWith('[')) {
      try {
        return JSON.parse(rawBody);
      } catch (parseError) {
        console.error(`API JSON parse error: ${url}`, parseError, preview);
        return null;
      }
    }

    console.error(`API Response not JSON: ${url}`, preview);
    return null;
  } catch (error) {
    // Cancellation is an expected lifecycle event (for example, closing the
    // Stock panel). Let the caller ignore it instead of logging a false API
    // failure and turning it into a second "Failed to fetch media" error.
    if (error?.name === 'AbortError') throw error;
    console.error(`API Call failed: ${url}`, error);
    return null;
  }
};
